import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import { getInitials } from '../lib/utils';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Minimize2,
  Maximize2,
  Sparkles,
  FileText,
  Video,
  ExternalLink
} from 'lucide-react';

const RAG_URL = `${process.env.REACT_APP_API_URL}/rag`;
const LOGO_URL = '/estrapat-logo.png';

// Lista de documentos fuente usados en una respuesta
const SourcesList = ({ fuentes }) => {
  if (!fuentes || fuentes.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-1">
      {fuentes.map((fuente, idx) => {
        const isVideo = fuente.tipo === 'mp4';
        const Icon = isVideo ? Video : FileText;
        const badge = (
          <div className="flex items-center gap-1 text-xs bg-muted/60 rounded px-2 py-0.5 max-w-full">
            <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate text-muted-foreground">{fuente.nombre}</span>
            {fuente.drive_url && (
              <ExternalLink className="h-3 w-3 shrink-0 text-blue-500 ml-0.5" />
            )}
          </div>
        );

        return fuente.drive_url ? (
          <a
            key={idx}
            href={fuente.drive_url}
            target="_blank"
            rel="noopener noreferrer"
            title={isVideo ? 'Ver video' : 'Abrir documento'}
            className="hover:opacity-80 transition-opacity self-start"
          >
            {badge}
          </a>
        ) : (
          <div key={idx} className="self-start">{badge}</div>
        );
      })}
    </div>
  );
};

export const FloatingChat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: '¡Hola! Soy el asistente de EstraPat. Puedo responder preguntas sobre los documentos de tu aseguradora: coberturas, procesos, productos y más. ¿En qué te ayudo?',
      fuentes: [],
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Ref al div centinela al final de los mensajes — scrollIntoView lo lleva al fondo
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, isOpen]);

  // Visible para todos los roles excepto viewer
  if (!user || user.role === 'viewer') {
    return null;
  }

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      fuentes: [],
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const lastBotMsg = [...messages].reverse().find(m => m.role === 'assistant')?.content || null;
      const response = await axios.post(`${RAG_URL}/query`, {
        pregunta: userMessage.content,
        last_bot_msg: lastBotMsg,
      });

      const allFuentes = [
        ...(response.data.fuente ? [response.data.fuente] : []),
        ...(response.data.fuentes_adicionales || []),
      ];
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.respuesta,
        fuentes: allFuentes,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('RAG chat error:', error);
      toast.error('Error al consultar el asistente');

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Lo siento, ocurrió un error al procesar tu consulta. Por favor intenta de nuevo.',
        fuentes: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([{
      id: '1',
      role: 'assistant',
      content: '¡Hola! Soy el asistente de EstraPat. Puedo responder preguntas sobre los documentos de tu aseguradora: coberturas, procesos, productos y más. ¿En qué te ayudo?',
      fuentes: [],
    }]);
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-[#1a365d] hover:bg-[#1a365d]/90 z-50"
        data-testid="floating-chat-btn"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </Button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 bg-card border rounded-2xl shadow-2xl flex flex-col transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[560px]'
      }`}
      data-testid="floating-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-[#1a365d] rounded-t-2xl shrink-0">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={LOGO_URL} />
            <AvatarFallback className="bg-white text-[#1a365d]">
              <Sparkles className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="text-white">
            <p className="font-medium text-sm">Asistente EstraPat</p>
            {!isMinimized && (
              <p className="text-xs text-white/70">Basado en tus documentos</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/20"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/20"
            onClick={() => setIsOpen(false)}
            data-testid="close-chat-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages — div nativo para scroll confiable */}
          <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
            <div className="flex flex-col gap-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <Avatar className="h-7 w-7 mt-1 shrink-0">
                      <AvatarImage src={LOGO_URL} />
                      <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                        <Sparkles className="h-3 w-3" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="max-w-[78%] min-w-0">
                    <div
                      className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        message.role === 'user'
                          ? 'bg-[#1a365d] text-white rounded-br-none'
                          : 'bg-muted rounded-bl-none'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                    </div>
                    {message.role === 'assistant' && (
                      <SourcesList fuentes={message.fuentes} />
                    )}
                  </div>
                  {message.role === 'user' && (
                    <Avatar className="h-7 w-7 mt-1 shrink-0">
                      <AvatarFallback className="bg-[#2d8659] text-white text-xs">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              ))}

              {/* Indicador de carga */}
              {loading && (
                <div className="flex gap-2 justify-start">
                  <Avatar className="h-7 w-7 mt-1 shrink-0">
                    <AvatarImage src={LOGO_URL} />
                    <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                      <Sparkles className="h-3 w-3" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="bg-muted rounded-2xl rounded-bl-none px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs text-muted-foreground">Buscando en documentos...</span>
                  </div>
                </div>
              )}

              {/* Centinela para scroll automático */}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input — siempre visible al fondo */}
          <div className="shrink-0 p-3 border-t bg-card rounded-b-2xl">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Pregunta sobre coberturas, productos..."
                className="flex-1 text-sm"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                size="icon"
                className="bg-[#1a365d] hover:bg-[#1a365d]/90 shrink-0"
                data-testid="send-message-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={handleNewChat}
              >
                Nueva conversación
              </Button>
              <span className="text-xs text-muted-foreground">Solo info de documentos</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
